// stores/fetchDataStore.ts
import moment from "moment-timezone";
import { defineStore } from "pinia";
import { useFilterStore } from "./filterStore";
import { useActivityStore } from "./activityStore";
import { useCatchupStore } from "./catchupStore";
import { useMasteryStore } from "./masteryStore";
import { useConceptsStore } from "./conceptsStore";
import { useSchoolStore } from "./schoolStore";
import { useLearnerStore } from "./learnerStore";
import { useUserStore } from "./userStore";
import { useLicenseStore } from "./licenseStore";
import { useRouter } from "vue-router";

import router from "@/router";
import { filter } from "lodash";
import { useLingoStore } from "./lingoStore";

export const useDataStore = defineStore({
  id: "dataStore",
  state: () => ({
    loading: false,
    loadingMessage: "",
  }),
  actions: {
    async buildPayload(includeDates = false, includeSubject = true) {
      const filterStore = useFilterStore();

      const payload: any = {
        school: filterStore.selectedFilters.school,
        grade: filterStore.selectedFilters.gradeLevel,
      };

      if (filterStore.selectedFilters.classGroup.length > 0) {
        payload.classGroup = filterStore.selectedFilters.classGroup;
      }

      if (includeSubject) {
        payload.subject = filterStore.selectedFilters.subject;
      }

      if (includeDates) {
        // Get todays date
        const today = moment().format("YYYY-MM-DD");
        // Get the date 30 days ago
        const thirtyDaysAgo = moment()
          .subtract(30, "days")
          .format("YYYY-MM-DD");
        payload.startDate = thirtyDaysAgo;
        payload.endDate = today;
      }

      // Add the user timezone to the payload
      payload.timezone = moment.tz.guess();

      return payload;
    },
    // Method to fetch initial data after login
    async fetchInitialData(initialiseFilters = false) {
      try {
        const filterStore = useFilterStore();
        const catchupStore = useCatchupStore();
        const masteryStore = useMasteryStore();
        const conceptsStore = useConceptsStore();
        const activityStore = useActivityStore();
        const licenseStore = useLicenseStore();
        const lingoStore = useLingoStore();
        const learnerStore = useLearnerStore();

        this.loading = true;
        this.loadingMessage = "Fetching your data...";

        // Fetch filter options and set default values
        // This MUST succeed on initial login — errors will propagate to the caller
        if (initialiseFilters) {
          await filterStore.fetchFilterOptions(initialiseFilters);
        }

        await this.fetchSchoolStructureData(initialiseFilters);

        // Use the buildPayload action to generate payloads
        const payload = await this.buildPayload(true); // Payload with dates
        const noDatesPayload = await this.buildPayload(); // Payload without dates
        const loginDataPayload = await this.buildPayload(true, false); // Payload with dates for login data

        // Fetch data from various stores
        activityStore.fetchActivityData(payload);
        activityStore.fetchDailyTasksData(payload);
        activityStore.fetchActivitySummaryPerStudent(payload);
        activityStore.fetchLoginActivityData(loginDataPayload);
        activityStore.fetchActivitySummaryPerClassData(noDatesPayload);

        // Catchup Store
        catchupStore.fetchAverageGapsData(noDatesPayload);
        catchupStore.fetchReflectionQuizzesAverageScoreData(payload);

        // mastery Store - API will set date defaults
        masteryStore.fetchStudentProgressData(noDatesPayload);
        masteryStore.fetchAssessmentMasteryData(noDatesPayload);
        masteryStore.fetchBaselineCompletionData(noDatesPayload);

        // Concepts tab overview (per-concept proficiency bars + table + counter)
        conceptsStore.fetchConceptsOverview(noDatesPayload);

        // Get learner IDs for lingo data fetch
        const learnerIds = learnerStore.getSchoolLearners.map(learner => learner._id);
        lingoStore.fetchStudentLingoData({ studentIds: learnerIds });

        //get school license data
        licenseStore.fetchLicenses(filterStore.selectedFilters.school);

      } catch (error) {
        console.error("Error fetching initial data:", error);
        throw error;
      } finally {
        this.loading = false;
        this.loadingMessage = "";
      }
    },
    async fetchNewData() {
      // Save current filters
      const filterStore = useFilterStore();
      const currentFilters = filterStore.selectedFilters;

      await this.fetchInitialData();
      await filterStore.fetchFilterOptions();

      // Restore filters
      filterStore.selectedFilters = currentFilters;

      // Apply filters
      await filterStore.applyFilters();
    },
    async fetchSchoolStructureData(initialDataLoad = false) {
      const userStore = useUserStore();
      const filterStore = useFilterStore();
      const schoolStore = useSchoolStore();
      const learnerStore = useLearnerStore();

      const school = filterStore.selectedSchool;

      console.log('[fetchSchoolStructureData] selectedSchool:', school?._id, school?.name, '| selectedFilters.school:', filterStore.selectedFilters.school, '| selectedFilters.schoolGroup:', filterStore.selectedFilters.schoolGroup);

      if (!school?._id) {
        const msg = "fetchSchoolStructureData: No school selected, skipping.";
        console.warn(msg);
        if (initialDataLoad) {
          throw new Error(msg);
        }
        return;
      }

      this.loading = true;
      this.loadingMessage = "Fetching your data...";

      console.log('[fetchSchoolStructureData] Fetching school:', school._id);
      await schoolStore.fetchSchool(school._id);
      await schoolStore.fetchSchoolStructure(school._id);
      console.log('[fetchSchoolStructureData] Fetching learners for school:', school._id);
      await learnerStore.fetchSchoolLearners(school._id);
      console.log('[fetchSchoolStructureData] Done. Learner count:', learnerStore.schoolLearners?.length);

      this.loading = false;
      this.loadingMessage = "";

      // if login and teacher
      if (initialDataLoad) {
        const defaultHome =
          schoolStore.lingoMode === "v2" && schoolStore.isEnglishOnlySchool
            ? "/main/lingo-data"
            : "/main/overview";

        console.log("INITIAL DATA LOAD: ", initialDataLoad);
        const selectedClassGroupId = filterStore.selectedFilters.classGroup[0];

        console.log("SELECTED CLASS GROUP: ", selectedClassGroupId);

        if (userStore.isClassGroupAdmin && selectedClassGroupId) {
          // Check if the selected class group has learners
          const learners = learnerStore.getSchoolLearners.filter((learner) =>
            learner.classes &&
            learner.classes.some((cls) => cls._id === selectedClassGroupId)
          );

          console.log("LEARNERS: ", learners);

          if (learners.length === 0) {
            // Navigate to the students view
            router.push("/main/students");
          } else {
            // Navigate to the dashboard
            router.push(defaultHome);
          }
        } else {
          // Navigate to the dashboard
          router.push(defaultHome);
        }
      }
    },
    fetchAllClassesData() {
      const filterStore = useFilterStore();
      const activityStore = useActivityStore();

      const payload = {
        school: filterStore.selectedFilters.school,
        grade: filterStore.selectedFilters.gradeLevel,
        subject: filterStore.selectedFilters.subject,
        timezone: moment.tz.guess(),
      };

      // Fetch data from various stores
      activityStore.fetchActivitySummaryPerClassData(payload);
    },
  },
});
